import React from 'react';
import { Box, Typography, IconButton, makeStyles } from '@material-ui/core';
import { Delete, Add, Remove } from '@material-ui/icons';
import { IconContext } from 'react-icons';
import * as faIcons from 'react-icons/fa6';
import { useDispatch } from 'src/store';
import { removeFromCart, updateItemQuantity } from 'src/slices/cart';
import { CartItem as CartItemType } from 'src/slices/cart';
import { productTypeImgMapping } from 'src/views/auth/Payment/paymentConstants';
import { getNavigatorLanguage } from 'src/utils/locale';
import { Theme } from 'src/theme';

const useStyles = makeStyles((theme: Theme) => ({
  cartItem: {
    padding: theme.spacing(2, 1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: 'transparent',
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  topRow: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(1),
  },
  productIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 48,
    height: 48,
    marginRight: theme.spacing(1.5),
    flexShrink: 0,
  },
  productInfo: {
    flex: 1,
    minWidth: 0,
  },
  productName: {
    fontWeight: 600,
    fontSize: '1rem',
    lineHeight: 1.4,
    marginBottom: theme.spacing(0.5),
    color: theme.palette.text.primary,
  },
  productPrice: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
  },
  totalPrice: {
    fontWeight: 600,
    fontSize: '1rem',
    color: theme.palette.text.primary,
    marginLeft: theme.spacing(2),
  },
  bottomRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginLeft: theme.spacing(8), // Align with text above
  },
  quantityControls: {
    display: 'flex',
    alignItems: 'center',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    backgroundColor: theme.palette.background.paper,
  },
  quantityButton: {
    minWidth: 32,
    width: 32,
    height: 32,
    padding: 0,
    backgroundColor: 'transparent',
    border: 'none',
    color: theme.palette.text.secondary,
    fontSize: '1rem',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.text.primary,
    },
    '&:disabled': {
      color: theme.palette.action.disabled,
    },
  },
  quantity: {
    minWidth: 40,
    textAlign: 'center',
    fontWeight: 500,
    padding: theme.spacing(0, 1),
    fontSize: '1rem',
    borderLeft: `1px solid ${theme.palette.divider}`,
    borderRight: `1px solid ${theme.palette.divider}`,
  },
  deleteButton: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(0.5),
    '&:hover': {
      color: theme.palette.error.main,
      backgroundColor: theme.palette.error.light + '20',
    },
  },
}));

interface CartItemProps {
  item: CartItemType;
}

const CartItem: React.FC<CartItemProps> = ({ item }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const handleRemove = () => {
    dispatch(
      removeFromCart({
        productId: item.product.productId,
        interval: item.interval,
      })
    );
  };

  const handleQuantityChange = (newQuantity: number) => {
    dispatch(
      updateItemQuantity({
        productId: item.product.productId,
        interval: item.interval,
        quantity: newQuantity,
      })
    );
  };


  const Icon = item.product.icon && faIcons[item.product.icon] ? faIcons[item.product.icon] : null;
  const imgSrc =
    item.product.productType && productTypeImgMapping[item.product.productType]
      ? productTypeImgMapping[item.product.productType]
      : 'subscription';

  const unitPrice = new Intl.NumberFormat(getNavigatorLanguage(), {
    style: 'currency',
    currency: item.currency.toUpperCase(),
  }).format(item.price);

  const totalPrice = new Intl.NumberFormat(getNavigatorLanguage(), {
    style: 'currency',
    currency: item.currency.toUpperCase(),
  }).format(item.price * item.quantity);

  return (
    <Box className={classes.cartItem}>
      {/* Top Row: Icon, Product Name/Price, Total Price */}
      <Box className={classes.topRow}>
        <Box className={classes.productIcon}>
          {Icon ? (
            <IconContext.Provider value={{ size: '1.5em', color: '#1976d2' }}>
              <Icon />
            </IconContext.Provider>
          ) : (
            <img
              width="32px"
              height="32px"
              src={`/static/productIcons/${imgSrc}.svg`}
              alt={item.product.name}
            />
          )}
        </Box>

        <Box className={classes.productInfo}>
          <Typography className={classes.productName}>{item.product.name}</Typography>
          <Typography className={classes.productPrice}>
            {unitPrice}
            {item.interval && ` / ${item.interval}`}
          </Typography>
        </Box>

        <Typography className={classes.totalPrice}>{totalPrice}</Typography>
      </Box>

      {/* Bottom Row: Quantity Controls and Delete */}
      <Box className={classes.bottomRow}>
        <Box className={classes.quantityControls}>
          <IconButton
            className={classes.quantityButton}
            onClick={() => handleQuantityChange(item.quantity - 1)}
            disabled={item.quantity <= 1}
          >
            <Remove />
          </IconButton>

          <Typography className={classes.quantity}>{item.quantity}</Typography>

          <IconButton
            className={classes.quantityButton}
            onClick={() => handleQuantityChange(item.quantity + 1)}
          >
            <Add />
          </IconButton>
        </Box>

        <IconButton onClick={handleRemove} className={classes.deleteButton}>
          <Delete />
        </IconButton>
      </Box>
    </Box>
  );
};

export default CartItem;

